import ReactMarkdown from "react-markdown";

import remarkGfm from "remark-gfm";
import { css } from "@emotion/react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";

import { colors, ExpandMoreIcon } from "@dotnet/telematics-design-system";

import { body1BoldStyles } from "shared";

import TextLink, { TEXT_LINK_SIZES } from "../TextLink";
import { NewsProps } from "./types";

const LinkInNewTab = ({ href, children }: any) => {
    return (
        <TextLink href={href} size={TEXT_LINK_SIZES.Small} target="_blank">
            {children}
        </TextLink>
    );
};

const AccordionCustom = ({
    message,
    index,
    expandedPanel,
    isLast,
    rootPadding,
    onCollapse
}: NewsProps) => {
    const { gray200, gray700 } = colors;

    const { title, body } = message;

    return (
        <Accordion
            elevation={0}
            data-testid="news-accordion"
            expanded={expandedPanel === `panel${index}`}
            onChange={(_, expanded) => onCollapse(`panel${index}`, expanded)}
            disableGutters
            css={css({
                borderBottom: isLast ? "0" : `1px ${gray200} solid`,

                "&.MuiAccordion-root:before": {
                    backgroundColor: "transparent"
                },

                "& .MuiAccordionSummary-root": {
                    padding: rootPadding
                },

                "& .MuiAccordionSummary-content": {
                    margin: index === 0 ? "0 0 12px 0" : "12px 0"
                }
            })}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="accordionHeader"
                key={index}
            >
                <div
                    css={css([
                        {
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "column",

                            borderRadius: "8px",

                            paddingTop: "6px",
                            paddingBottom: "2px",

                            p: {
                                fontWeight: 600,
                                textTransform: "uppercase"
                            },

                            "p:first-of-type": {
                                fontSize: "10px",
                                lineHeight: "12px",
                                letterSpacing: "1px",

                                paddingLeft: "8px",
                                paddingRight: "8px"
                            },

                            "p:last-of-type": {
                                fontSize: "14px",
                                lineHeight: "20px",
                                letterSpacing: "0.1px"
                            }
                        }
                    ])}
                ></div>

                <div
                    css={css({
                        paddingLeft: "8px",
                        display: "flex",
                        alignItems: "center",

                        p: {
                            ...body1BoldStyles
                        }
                    })}
                >
                    <Typography>{title}</Typography>
                </div>
            </AccordionSummary>

            <AccordionDetails
                css={css({
                    "span, a": {
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        margin: 0
                    },

                    span: { color: gray700 },

                    strong: {
                        fontWeight: 700
                    },

                    "h1, h2, h3, h4, h5, h6": {
                        fontSize: "14px",
                        fontWeight: 600,
                        margin: "revert"
                    },

                    ul: {
                        marginBlock: "1em",
                        marginInline: "0px",
                        paddingInlineStart: "40px"
                    },

                    li: {
                        listStyle: "unset"
                    }
                })}
            >
                <Typography component="span">
                    <ReactMarkdown
                        children={body}
                        remarkPlugins={[remarkGfm]}
                        components={{ a: LinkInNewTab }}
                    />
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
};

export * from "./types";

export default AccordionCustom;
